import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
import { useCallback, useMemo, useRef } from "react";

export interface HeldIdempotencyKey {
  /**
   * Returns the idempotency key for the given payload fingerprint: the same
   * key while the fingerprint is unchanged (retries replay the original
   * response), a freshly generated UUID once the payload changes.
   */
  keyFor(fingerprint: string): string;
  /** Drops the held key (terminal states). */
  reset(): void;
}

/**
 * Holds one idempotency key per payload fingerprint for POSTs that require
 * an Idempotency-Key header (registration completion, invitation-producing
 * admin actions): identical payloads reuse the held key so the server
 * replays, a changed payload gets a fresh UUID.
 */
export function useHeldIdempotencyKey(): HeldIdempotencyKey {
  const heldRef = useRef<{ fingerprint: string; key: string } | null>(null);

  const keyFor = useCallback((fingerprint: string): string => {
    if (heldRef.current?.fingerprint !== fingerprint) {
      heldRef.current = { fingerprint, key: crypto.randomUUID() };
    }
    return heldRef.current.key;
  }, []);

  const reset = useCallback((): void => {
    heldRef.current = null;
  }, []);

  return useMemo(() => ({ keyFor, reset }), [keyFor, reset]);
}

/**
 * Canonical JSON serialization for idempotency fingerprints: object keys are
 * sorted and `undefined` entries dropped so semantically identical payloads
 * always produce the same fingerprint regardless of key order.
 */
export function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableStringify(entry)).join(",")}]`;
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue !== undefined)
      .sort(([left], [right]) => (left < right ? -1 : 1))
      .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringify(entryValue)}`);
    return `{${entries.join(",")}}`;
  }

  return JSON.stringify(value) ?? "null";
}

/**
 * Non-reversible fingerprint for payloads that carry secrets: the holder then
 * retains a SHA-256 hex digest instead of the serialized plaintext, while
 * identical payloads still map to the same held key. Synchronous on purpose
 * (SubtleCrypto is Promise-only) so submit paths keep no plaintext-bearing
 * continuation alive; the encoded bytes are zeroed before returning.
 */
export function digestFingerprint(value: unknown): string {
  const bytes = utf8ToBytes(stableStringify(value));
  try {
    return bytesToHex(sha256(bytes));
  } finally {
    bytes.fill(0);
  }
}
